<template>
  <div
    class="aspect-video w-full overflow-hidden bg-static-default-mid"
    :class="{ 'pointer-events-none': !controls, 'rounded-lg': rounded }"
  >
    <iframe
      ref="youtubeIframe"
      allow="autoplay; encrypted-media"
      allowfullscreen
      class="h-full w-full"
      frameborder="0"
      referrerpolicy="strict-origin-when-cross-origin"
      :src="embedUrl"
      :title
      @load="emit('load')"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, useTemplateRef } from 'vue'

import {
  YOUTUBE_VIDEO_ORIGIN,
  useYouTubeIframe,
} from '../composables/useYouTubeIframe'

const {
  videoId,
  title,
  controls = true,
  rounded = true,
  muted = true,
  loop = false,
} = defineProps<{
  videoId: string
  title: string
  /**
   * Whether to show YouTube's own player controls. Set to `false` for a
   * decorative, non-interactive preview (e.g. the collapsed widget's mini
   * player) — also makes the embed click-through (`pointer-events-none`) so
   * clicks reach the surrounding UI instead of being swallowed by the
   * cross-origin iframe.
   */
  controls?: boolean
  /**
   * Whether to round the embed's corners. The collapsed widget's mini player
   * sits flush inside its own rounded container, so it opts out to avoid a
   * double-rounded-corner look.
   */
  rounded?: boolean
  /**
   * Whether the embed starts muted. Defaults to `true` — required for the
   * collapsed mini player, which autoplays without a user gesture (browser
   * autoplay policy blocks unmuted autoplay in that case). The expanded
   * player opts out: opening the drawer is itself a user gesture, so
   * unmuted autoplay is allowed.
   */
  muted?: boolean
  /**
   * Whether to loop back to the start when the video ends. Defaults to
   * `false` — the expanded player has products timed against specific
   * moments, so it should actually end; the collapsed mini preview is purely
   * decorative and should keep playing indefinitely.
   */
  loop?: boolean
}>()

const emit = defineEmits<{
  (e: 'load'): void
  (e: 'start'): void
  (e: 'ended'): void
  (e: 'progress', percent: number): void
}>()

const youtubeIframe = useTemplateRef('youtubeIframe')

const embedUrl = computed(() => {
  const params = new URLSearchParams()

  // Starts playback as soon as the embed mounts.
  params.set('autoplay', '1')
  // Silent autoplay for the collapsed mini player (browsers block unmuted
  // autoplay without a user gesture); the expanded player opts out via the
  // `muted` prop, since opening the drawer is itself a user gesture.
  params.set('mute', muted ? '1' : '0')
  // Subtitles are burned into the live-shopping videos — keep YouTube's
  // captions off by default instead of showing a duplicate track.
  params.set('cc_load_policy', '0')
  // Hides the YouTube logo from the player controls.
  params.set('modestbranding', '1')
  // Disables the end-of-video related-videos overlay.
  params.set('rel', '0')
  // Plays inline instead of fullscreen on iOS.
  params.set('playsinline', '1')
  // Shows/hides YouTube's own player controls — off for the decorative,
  // non-interactive collapsed preview.
  params.set('controls', controls ? '1' : '0')
  // Enables the postMessage API the composable talks to for seek/progress.
  params.set('enablejsapi', '1')

  if (loop) {
    params.set('loop', '1')
    // YouTube only honors `loop=1` for a single video if `playlist` is set
    // to that same video's ID — undocumented but required quirk of the
    // IFrame Player API.
    params.set('playlist', videoId)
  }

  return `${YOUTUBE_VIDEO_ORIGIN}/embed/${videoId}?${params.toString()}`
})

// The postMessage bridge (command-sending, "listening" handshake, event
// listener lifecycle, progress milestone tracking) is handled by the shared
// `useYouTubeIframe` composable — extracted from Paul Melero's original
// postMessage implementation in `GalleryNew/YoutubeVideo.vue`.
const { seekTo, sendCommand, isPlaying } = useYouTubeIframe(youtubeIframe, {
  start: () => emit('start'),
  ended: () => emit('ended'),
  progress: (percent) => emit('progress', percent),
})

/**
 * Pauses and reports whether playback was actually running, so the caller
 * only resumes something it paused (not a manual pause by the visitor).
 */
function pause() {
  const wasPlaying = isPlaying.value
  sendCommand('pauseVideo')

  return wasPlaying
}

function resume() {
  sendCommand('playVideo')
}

defineExpose({ seekTo, pause, resume })
</script>
